import React, { useState } from 'react';
import { Button, Input, Card, Alert, Typography, Form } from 'antd';
import { KeyOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { verifyAccessCode, clearError } from '../store/authSlice';
import { RootState, AppDispatch } from '../store/store';
import { NimbusLogo } from './NimbusLogo';
import './AccessCodeLogin.css';

const { Title, Text } = Typography;

export const AccessCodeLogin: React.FC = () => {
  const [code, setCode] = useState('');
  const dispatch = useDispatch<AppDispatch>();
  const { isVerifying, verificationError } = useSelector((state: RootState) => state.auth);

  const isValidCodeFormat = /^[A-Za-z0-9_-]{43}$/.test(code.trim());

  const handleSubmit = async () => {
    if (!code.trim()) return;
    
    dispatch(clearError());
    const result = await dispatch(verifyAccessCode(code.trim()));
    
    if (verifyAccessCode.fulfilled.match(result)) {
      localStorage.setItem('auth_access_code', result.payload);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value);
    if (verificationError) {
      dispatch(clearError());
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isVerifying && isValidCodeFormat) {
      handleSubmit();
    }
  };

  return (
    <div className="access-code-login">
      <div className="login-container">
        <Card className="login-card">
          <div className="login-content">
            <div className="logo-section">
              <NimbusLogo />
              <Title level={2} className="login-title">
                Access Required
              </Title>
              <Text type="secondary" className="login-subtitle">
                Please enter your access code to continue
              </Text>
            </div>

            <Form layout="vertical" className="login-form">
              <Form.Item
                label="Access Code"
                validateStatus={verificationError ? 'error' : (code.length > 0 && !isValidCodeFormat) ? 'warning' : ''}
                help={verificationError || (code.length > 0 && !isValidCodeFormat ? 'Invalid access code format' : '')}
              >
                <Input
                  size="large"
                  prefix={<KeyOutlined />}
                  placeholder="XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                  value={code}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  disabled={isVerifying}
                  style={{ fontSize: '16px', height: '50px' }}
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  size="large"
                  block
                  loading={isVerifying}
                  disabled={!isValidCodeFormat}
                  onClick={handleSubmit}
                  className="grant-access-button"
                >
                  Grant Access
                </Button>
              </Form.Item>
            </Form>

            {verificationError && (
              <Alert
                message="Authentication Failed"
                description={verificationError}
                type="error"
                showIcon
                className="error-alert"
              />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};